import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DateValue } from "@internationalized/date";
import { parseDate } from "@internationalized/date";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { BarChart07, Edit05, LayoutAlt04 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  useAnalyticsDashboards,
  useReorderAnalyticsDashboards,
  useUpdateAnalyticsDashboard,
} from "@/api/analytics-dashboards";
import { DateRangePicker } from "@/components/application/date-picker/date-range-picker";
import { Button } from "@/components/base/buttons/button";
import { DateGranularitySelector } from "@/components/DateGranularitySelector";
import type { AnalyticsView } from "@/pages/analytics-dashboard/components/AnalyticsViewConfigurationModal";
import { AnalyticsViewConfigurationModal } from "@/pages/analytics-dashboard/components/AnalyticsViewConfigurationModal";
import { cx } from "@/utils/cx";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { AnalyticsOverview } from "./components/AnalyticsOverview";
import type { Dashboard } from "./components/DashboardConfigurationModal";
import { DashboardConfigurationModal } from "./components/DashboardConfigurationModal";
import { DashboardContent } from "./components/DashboardContent";
import { useDashboardDateFilter } from "./hooks/use-dashboard-date-filter";

type TimeGranularity = "day" | "week" | "month";

// Custom Sortable Tab Item
const SortableTabItem: React.FC<{
  tab: { id: string; label: string };
  isSelected: boolean;
  onSelect: () => void;
}> = ({ tab, isSelected, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tab.id,
    disabled: tab.id === "overview", // Disable dragging for Overview tab
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onSelect}
      className={cx(
        "z-10 flex h-max items-center justify-center gap-2 whitespace-nowrap transition duration-100 ease-linear px-3 py-2 text-sm font-semibold",
        "rounded-none border-b-2 border-transparent outline-focus-ring",
        isSelected
          ? "border-fg-brand-primary_alt text-brand-secondary"
          : "text-quaternary hover:border-fg-brand-primary_alt hover:text-brand-secondary",
        tab.id !== "overview" && "cursor-grab active:cursor-grabbing",
      )}
    >
      {tab.label}
    </button>
  );
};

export const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as { tab?: string; start?: string; end?: string };

  // Initialize selectedTab from URL or localStorage
  const [selectedTab, setSelectedTab] = useState<string>(() => {
    // First try URL
    if (searchParams.tab) return searchParams.tab;
    // Then try localStorage
    const stored = localStorage.getItem("analytics-dashboard-selected-tab");
    return stored || "overview";
  });
  const [timeGranularity, setTimeGranularity] = useState<TimeGranularity>("week");
  const { dateRange, isApplied, changeDate, limits } = useDashboardDateFilter();
  const [tempDateRange, setTempDateRange] = useState<{ start: DateValue; end: DateValue } | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch dashboards from database
  const { data: customDashboards = [], isLoading: isDashboardsLoading } = useAnalyticsDashboards();
  const updateDashboardMutation = useUpdateAnalyticsDashboard();
  const reorderDashboardsMutation = useReorderAnalyticsDashboards();

  // Get custom tab order from localStorage
  const [tabOrder, setTabOrder] = useState<string[]>(() => {
    const stored = localStorage.getItem("analytics-dashboard-tab-order");
    return stored ? JSON.parse(stored) : [];
  });

  const [editingDashboard, setEditingDashboard] = useState<Dashboard | null>(null);
  // const [isJsonExpanded, setIsJsonExpanded] = useState(false); // Commented out with JSON viewer
  const [isEditViewModalOpen, setIsEditViewModalOpen] = useState(false);
  const [editingView, setEditingView] = useState<AnalyticsView | null>(null);

  // Use ref to track if we're programmatically changing the date (to avoid save loop)
  const isRestoringDateRange = useRef(false);
  const isResettingDateRange = useRef(false);
  const isApplyingDateRange = useRef(false);
  const hasShownDashboardNotFoundToast = useRef(false);
  const isCreatingDashboard = useRef(false);

  // Custom clear function that resets to default (unset state)
  const handleClearDateRange = () => {
    // Set flag to prevent restoration
    isResettingDateRange.current = true;

    // Clear from localStorage for current tab first
    const storageKey = `analytics-dashboard-daterange-${selectedTab}`;
    localStorage.removeItem(storageKey);

    // Clear from URL (keep only tab param)
    navigate({
      search: { tab: selectedTab } as any,
      replace: true,
    });

    // Clear the date range (set to null in Zustand store)
    changeDate(null);

    // Reset flag after operations complete
    setTimeout(() => {
      isResettingDateRange.current = false;
    }, 100);
  };

  // Sync selectedTab and dateRange to URL and localStorage
  useEffect(() => {
    // Build search params
    const params: { tab: string; start?: string; end?: string } = { tab: selectedTab };

    if (dateRange && isApplied) {
      params.start = dateRange.start.toString();
      params.end = dateRange.end.toString();
    }

    // Update URL
    navigate({
      search: params as any,
      replace: true,
    });

    // Update localStorage for tab
    localStorage.setItem("analytics-dashboard-selected-tab", selectedTab);
  }, [selectedTab, dateRange, isApplied, navigate]);

  // Save date range to localStorage when it changes (per dashboard)
  useEffect(() => {
    // Don't save if we're restoring from storage
    if (dateRange && isApplied && !isRestoringDateRange.current) {
      const storageKey = `analytics-dashboard-daterange-${selectedTab}`;
      const startStr = dateRange.start.toString();
      const endStr = dateRange.end.toString();
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          start: startStr,
          end: endStr,
        }),
      );
    }
  }, [dateRange, isApplied, selectedTab]);

  // Restore date range from URL or localStorage when component mounts or tab changes
  useEffect(() => {
    // Don't restore if we're in the middle of resetting or applying a new date range
    if (isResettingDateRange.current || isApplyingDateRange.current) return;

    // First priority: URL params
    if (searchParams.start && searchParams.end) {
      try {
        const currentStart = dateRange?.start.toString();
        const currentEnd = dateRange?.end.toString();

        if (currentStart !== searchParams.start || currentEnd !== searchParams.end) {
          isRestoringDateRange.current = true;
          const parsedStart = parseDate(searchParams.start);
          const parsedEnd = parseDate(searchParams.end);
          changeDate({ start: parsedStart, end: parsedEnd });
          setTimeout(() => {
            isRestoringDateRange.current = false;
          }, 100);
        }
      } catch (error) {
        console.error("Failed to restore date range from URL:", error);
      }
      return;
    }

    // Second priority: localStorage
    const storageKey = `analytics-dashboard-daterange-${selectedTab}`;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const { start, end } = JSON.parse(stored);
        const currentStart = dateRange?.start.toString();
        const currentEnd = dateRange?.end.toString();

        if (currentStart !== start || currentEnd !== end) {
          isRestoringDateRange.current = true;
          const parsedStart = parseDate(start);
          const parsedEnd = parseDate(end);
          changeDate({ start: parsedStart, end: parsedEnd });
          setTimeout(() => {
            isRestoringDateRange.current = false;
          }, 100);
        }
      } catch (error) {
        console.error("Failed to restore date range from localStorage:", error);
      }
    }
  }, [selectedTab, searchParams.start, searchParams.end, dateRange, changeDate]);

  // Close modal (React Query will auto-refresh via cache invalidation)
  const handleModalClose = () => {
    setIsCreateModalOpen(false);
    setEditingDashboard(null);
  };

  // Handle save dashboard and switch to it
  const handleSaveDashboard = (dashboardId: string) => {
    isCreatingDashboard.current = true;
    setSelectedTab(dashboardId);
    // Reset flag after React Query has time to update
    setTimeout(() => {
      isCreatingDashboard.current = false;
    }, 1000);
  };

  // Map time granularity to API timePeriod
  // "day" uses "daily" timePeriod (daily data points)
  // "week" uses "weekly" timePeriod (weekly data points)
  // "month" uses "monthly" timePeriod (monthly data points)
  const timePeriod: "daily" | "weekly" | "monthly" =
    timeGranularity === "day" ? "daily" : timeGranularity === "month" ? "monthly" : "weekly";

  // Build tabs array with Overview + custom dashboards
  // Database order is the source of truth (sorted by `order` column)
  // localStorage tabOrder is used for optimistic updates during drag-and-drop
  const sortedDashboards = useMemo(() => {
    // If no local reordering has happened, use database order
    if (tabOrder.length === 0) return customDashboards;

    // Use localStorage order for optimistic UI updates
    return [...customDashboards].sort((a, b) => {
      const aIndex = tabOrder.indexOf(a.id);
      const bIndex = tabOrder.indexOf(b.id);

      // If both are in tabOrder, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only one is in tabOrder, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // If neither is in tabOrder, keep database order
      return 0;
    });
  }, [customDashboards, tabOrder]);

  const tabs = [
    {
      id: "overview",
      label: "Overview",
    },
    ...sortedDashboards.map((dashboard) => ({
      id: dashboard.id,
      label: dashboard.name,
    })),
  ];

  // Save tab order to localStorage when it changes
  useEffect(() => {
    if (tabOrder.length > 0) {
      localStorage.setItem("analytics-dashboard-tab-order", JSON.stringify(tabOrder));
    }
  }, [tabOrder]);

  // Validate selected tab exists, redirect to overview if not found
  useEffect(() => {
    // Skip if we're on overview
    if (selectedTab === "overview") {
      hasShownDashboardNotFoundToast.current = false;
      return;
    }

    // Skip validation if we're in the process of creating a new dashboard
    if (isCreatingDashboard.current) {
      return;
    }

    // Skip validation while dashboards are still loading
    if (isDashboardsLoading) {
      return;
    }

    // Check if the selected tab exists in the dashboards list
    const tabExists = customDashboards.some((d) => d.id === selectedTab);

    if (!tabExists && !hasShownDashboardNotFoundToast.current) {
      // Dashboard not found, redirect to overview
      hasShownDashboardNotFoundToast.current = true;
      toast.warning("Dashboard not found", {
        description: "This dashboard may have been deleted. Redirecting to overview.",
      });
      setSelectedTab("overview");
    }
  }, [selectedTab, customDashboards, isDashboardsLoading]);

  // Set up sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor),
  );

  // Handle drag end - reorder tabs and persist to database
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Don't allow dragging Overview tab
    if (active.id === "overview" || over.id === "overview") return;

    // Get current dashboard IDs (excluding overview)
    const dashboardIds = sortedDashboards.map((d) => d.id);
    const oldIndex = dashboardIds.indexOf(active.id as string);
    const newIndex = dashboardIds.indexOf(over.id as string);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder the array locally
    const newOrder = arrayMove(dashboardIds, oldIndex, newIndex);
    setTabOrder(newOrder);

    // Persist to database - map to { id, order } format
    const dashboardOrders = newOrder.map((id, index) => ({
      id,
      order: index,
    }));

    reorderDashboardsMutation.mutate(dashboardOrders);
  };

  // Handle edit dashboard
  const handleEditDashboard = () => {
    const dashboard = customDashboards.find((d) => d.id === selectedTab);
    if (dashboard) {
      setEditingDashboard(dashboard);
      setIsCreateModalOpen(true);
    }
  };

  // Handle dashboard deletion
  const handleDeleteDashboard = (deletedDashboardId: string) => {
    // Find the index of the deleted dashboard
    const deletedIndex = customDashboards.findIndex((d) => d.id === deletedDashboardId);

    if (deletedIndex === -1) return;

    // Determine which dashboard to select after deletion
    if (customDashboards.length === 1) {
      // If this was the last custom dashboard, go back to overview
      setSelectedTab("overview");
    } else {
      // Always select the previous dashboard (at deletedIndex - 1)
      // If deleting the first dashboard (index 0), select what will become the new first dashboard
      const targetIndex = deletedIndex === 0 ? 0 : deletedIndex - 1;
      const targetDashboard =
        deletedIndex === 0
          ? customDashboards[1] // Next dashboard becomes first
          : customDashboards[targetIndex]; // Previous dashboard
      setSelectedTab(targetDashboard.id);
    }
    // Database deletion happens in DashboardConfigurationModal, React Query will auto-refresh
  };

  // Handle edit widget in view mode
  const handleEditWidgetInView = (viewId: string) => {
    const currentDashboard = customDashboards.find((d) => d.id === selectedTab);
    if (!currentDashboard) return;

    const view = currentDashboard.views.find((v) => v.id === viewId);
    if (view) {
      setEditingView(view);
      setIsEditViewModalOpen(true);
    }
  };

  // Handle update view in view mode
  const handleUpdateViewInView = async (updatedView: AnalyticsView) => {
    const currentDashboard = customDashboards.find((d) => d.id === selectedTab);
    if (!currentDashboard) return;

    // Update the view in the dashboard's views array
    const updatedViews = currentDashboard.views.map((v) => (v.id === updatedView.id ? updatedView : v));

    // Save to database
    await updateDashboardMutation.mutateAsync({
      id: currentDashboard.id,
      input: {
        spec: {
          name: currentDashboard.name,
          views: updatedViews.map((v) => ({
            id: v.id,
            name: v.name,
            layout: v.layout,
            metrics: v.metrics,
          })),
          layout: currentDashboard.layout,
        },
      },
    });

    // Close modal
    setIsEditViewModalOpen(false);
    setEditingView(null);
  };

  // Handle delete widget in view mode
  const handleDeleteWidgetInView = async (viewId: string) => {
    const currentDashboard = customDashboards.find((d) => d.id === selectedTab);
    if (!currentDashboard) return;

    // Remove from views
    const updatedViews = currentDashboard.views.filter((v) => v.id !== viewId);

    // Save to database
    await updateDashboardMutation.mutateAsync({
      id: currentDashboard.id,
      input: {
        spec: {
          name: currentDashboard.name,
          views: updatedViews.map((v) => ({
            id: v.id,
            name: v.name,
            layout: v.layout,
            metrics: v.metrics,
          })),
          layout: currentDashboard.layout,
        },
      },
    });
  };

  return (
    <div
      className={cx(
        "w-full h-full overflow-y-scroll",
        "inline-flex flex-col justify-start items-center gap-12", // Internal layout
        "bg-linear-to-b from-[#ffc27a]/20 via-[#c3a5f0]/20 to-white", // Background gradient
      )}
    >
      <div className={cx(LAYOUT_CONSTRAINTS.CONTAINER_WIDTH, "flex flex-col gap-8 px-16 pt-16 pb-8")}>
        {/* Page Header */}
        <div className="flex flex-col gap-8">
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-80 flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <BarChart07 className="w-5 h-5 text-secondary" />
                <h1 className="text-md font-medium text-secondary">Performance Overview</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* <Button color="secondary" iconLeading={Calendar} onClick={setLastWeek}>
                Last week
              </Button> */}
              <Button color="primary" iconLeading={LayoutAlt04} onClick={() => setIsCreateModalOpen(true)}>
                Create new Dashboard view
              </Button>
            </div>
          </div>

          {/* Tabs Navigation with Drag and Drop */}
          <div className="tabs-scrollbar overflow-x-auto border-b border-[#E5DCD2]">
            <style>
              {`
                /* Custom scrollbar styling for tabs */
                .tabs-scrollbar::-webkit-scrollbar {
                  height: 4px;
                }
                .tabs-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .tabs-scrollbar::-webkit-scrollbar-thumb {
                  background: #D5D7DA;
                  border-radius: 2px;
                }
                .tabs-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: #A4A7AE;
                }
              `}
            </style>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={tabs.map((t) => t.id)} strategy={horizontalListSortingStrategy}>
                <div className="flex gap-0" role="tablist">
                  {tabs.map((tab) => (
                    <SortableTabItem
                      key={tab.id}
                      tab={tab}
                      isSelected={selectedTab === tab.id}
                      onSelect={() => setSelectedTab(tab.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Time Selection Controls */}
        <div className="flex justify-between items-center gap-6">
          {/* Edit Dashboard Button - only show for custom dashboards */}
          {selectedTab !== "overview" ? (
            <Button color="secondary" iconLeading={Edit05} onClick={handleEditDashboard}>
              Edit view
            </Button>
          ) : (
            <div />
          )}

          <div className="flex items-start gap-3">
            <DateRangePicker
              isActive={isApplied}
              textClassName="text-sm font-semibold"
              buttonClassName="h-10"
              aria-label="Date range picker"
              shouldCloseOnSelect={false}
              selected={dateRange}
              value={tempDateRange || dateRange}
              onChange={setTempDateRange}
              onReset={handleClearDateRange}
              onApply={() => {
                isApplyingDateRange.current = true;
                changeDate(tempDateRange);
                setTimeout(() => {
                  isApplyingDateRange.current = false;
                }, 200);
              }}
              maxValue={limits?.maxDate}
              minValue={limits?.minDate}
              limits={limits}
              showPresets={true}
              resetText="Reset to last 31 days"
            />
            <DateGranularitySelector value={timeGranularity} onChange={setTimeGranularity} />
          </div>
        </div>

        {/* Dashboard Content - switches based on selected tab */}
        {selectedTab === "overview" && (
          <AnalyticsOverview timePeriod={timePeriod} dateRange={dateRange} limits={limits} />
        )}
        {selectedTab !== "overview" && (
          <div className="w-full flex flex-col gap-4 items-center">
            {/* Dashboard JSON Debug Display - Collapsible */}
            {/* <div className="w-full bg-gray-50 rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => setIsJsonExpanded(!isJsonExpanded)}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-100 transition-colors rounded-lg"
              >
                <span className="text-xs font-medium text-gray-600">Dashboard JSON Spec</span>
                {isJsonExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
              </button>
              {isJsonExpanded && (
                <div className="px-3 pb-3">
                  <pre className="text-xs text-gray-600 overflow-auto max-h-96 p-2 bg-white rounded border border-gray-200">
                    {JSON.stringify(
                      customDashboards.find((d) => d.id === selectedTab),
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
            </div> */}

            {/* Custom Dashboard Content */}
            <DashboardContent
              views={customDashboards.find((d) => d.id === selectedTab)?.views || []}
              layout={customDashboards.find((d) => d.id === selectedTab)?.layout || []}
              editable={false}
              onEditWidget={handleEditWidgetInView}
              onDeleteWidget={handleDeleteWidgetInView}
              timePeriod={timePeriod}
              dateRange={dateRange}
              timeGranularity={timeGranularity}
            />
          </div>
        )}
      </div>

      {/* Create Dashboard Modal */}
      <DashboardConfigurationModal
        isOpen={isCreateModalOpen}
        onClose={handleModalClose}
        onDelete={handleDeleteDashboard}
        onSave={handleSaveDashboard}
        existingDashboard={editingDashboard}
      />

      {/* Edit Analytics View Modal - for view mode */}
      <AnalyticsViewConfigurationModal
        isOpen={isEditViewModalOpen}
        onOpenChange={setIsEditViewModalOpen}
        onSave={handleUpdateViewInView}
        editMode={true}
        existingView={editingView || undefined}
      />
    </div>
  );
};

// Export as default for backward compatibility
export { AnalyticsDashboard as AnalyticsOverview };
